import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, videos, getVideosByCategory } from '@/mocks/videos';

const siteUrl = 'https://xiuxiushipin.com';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'lianai-richang');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredVideos = getVideosByCategory(activeSlug);
  const allSlugs = categories.map((c) => c.slug);

  const categorySchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${activeCategory.name} - 羞羞视频精选合集`,
      description: `${activeCategory.description}，精选${activeCategory.name}类热门短视频内容。`,
      url: `${siteUrl}/category/${activeSlug}`,
    },
  ];

  return (
    <>
      <SEO
        title={`${activeCategory.name}视频`}
        description={`${activeCategory.description}，羞羞视频精选合集为您精选${activeCategory.name}类最热门的短视频内容，每日更新，欢乐不断。`}
        keywords={`${activeCategory.name},${activeCategory.name}视频,羞羞视频,搞笑短视频,趣味视频`}
        canonicalUrl={`${siteUrl}/category/${activeSlug}`}
        schemaData={categorySchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-100">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '视频分类', path: '/category/lianai-richang' },
              { label: activeCategory.name },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                <i className={`${activeCategory.icon} text-xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                  {activeCategory.name}
                </h1>
                <p className="text-foreground-500 text-sm mt-0.5">
                  {activeCategory.description}
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mt-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 shadow-lg shadow-primary-500/20'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <p className="text-foreground-500 text-sm">
                共 <strong className="text-foreground-900">{filteredVideos.length}</strong> 个视频
              </p>
              <div className="flex items-center gap-2 text-xs text-foreground-400">
                <span>排序：</span>
                <button className="px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap cursor-pointer">
                  最热
                </button>
                <button className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-500 hover:text-foreground-700 whitespace-nowrap cursor-pointer">
                  最新
                </button>
              </div>
            </div>

            {filteredVideos.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-video-off-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频，请查看其他分类</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer border border-background-200/70"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.coverImage}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={`羞羞视频 - ${video.title}`}
                      />
                      <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-foreground-900/75 backdrop-blur-sm rounded-md text-background-50 text-xs font-mono">
                        {video.duration}
                      </div>
                      {video.isHot && (
                        <div className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 rounded-full text-background-50 text-xs font-medium">
                          热门
                        </div>
                      )}
                      {video.isNew && (
                        <div className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 rounded-full text-background-50 text-xs font-medium">
                          新上线
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h2 className="font-medium text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                        {video.title}
                      </h2>
                      <p className="text-foreground-500 text-xs line-clamp-2 mb-3 leading-relaxed">
                        {video.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {video.viewCount >= 10000 ? `${(video.viewCount / 10000).toFixed(1)}万` : video.viewCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-heart-line"></i>
                          {video.likeCount >= 10000 ? `${(video.likeCount / 10000).toFixed(1)}万` : video.likeCount}
                        </span>
                        <span>{video.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}